import { useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import type { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import DualSliderInput from 'LuxKit/Slider/DualSliderInput'
import Group from 'components/utils/Group'
import debounce from 'lodash.debounce'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import TabButton from 'LuxKit/Tabs/TabButton'
import { FlightsFiltersPriceType } from 'lib/flights/flightFilterTypes'

interface Props {
  items: Array<FilterPanelCheckItem>
  onFilterChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onPriceTypeChange: (price: FlightsFiltersPriceType) => void
  selectedMaxItem?: string
  selectedMinItem?: string
  priceType: FlightsFiltersPriceType
}

const STEP_NUM = 100

function FlightSingleFilterPrice(props: Props) {
  const intl = useIntl()
  const {
    items,
    onFilterChange,
    selectedMaxItem,
    selectedMinItem,
    priceType,
    onPriceTypeChange,
  } = props

  const maxNum = (items[items.length - 1]?.value as number) || 0
  const minNum = (items[0]?.value as number) || 0
  const min = Math.floor(minNum / STEP_NUM) * STEP_NUM
  const max = Math.ceil(maxNum / STEP_NUM) * STEP_NUM

  const [startValue, setStartValue] = useState(
    selectedMinItem ? parseInt(selectedMinItem) : min,
  )
  const [endValue, setEndValue] = useState(
    selectedMaxItem ? parseInt(selectedMaxItem) : max,
  )

  // When we reset all the filters the value will be undefined so we need to set the value to min again
  useEffect(() => {
    if (!selectedMinItem) {
      setStartValue(min)
    }
  }, [selectedMinItem, min])

  // When we reset all the filters the value will be undefined so we need to set the value to max again
  useEffect(() => {
    if (!selectedMaxItem) {
      setEndValue(max)
    }
  }, [selectedMaxItem, max])

  const handleStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartValue(e.target.valueAsNumber)
    onChange(e)
  }

  const handleEndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndValue(e.target.valueAsNumber)
    onChange(e)
  }

  const onChange = useMemo(() => {
    return debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      onFilterChange(e)
    }, 200)
  }, [onFilterChange])

  return (
    <Group direction="vertical" gap={16}>
      <TabButtonsGroup fit="grow">
        <TabButton
          size="small"
          isActive={priceType === FlightsFiltersPriceType.ADULT}
          onClick={() => onPriceTypeChange(FlightsFiltersPriceType.ADULT)}
        >
          {intl.formatMessage({
            id: 'SF1c4uS6a1gaPEDtoKdm',
            defaultMessage: 'Per adult',
            description: 'Tab label for per-adult price filter on flight search',
          })}
        </TabButton>

        <TabButton
          size="small"
          isActive={priceType === FlightsFiltersPriceType.TOTAL}
          onClick={() => onPriceTypeChange(FlightsFiltersPriceType.TOTAL)}
        >
          {intl.formatMessage({
            id: 'eKHf1w0g3uJqkBJLaEJN',
            defaultMessage: 'Total price',
            description: 'Tab label for total price filter on flight search in the flights section',
          })}
        </TabButton>
      </TabButtonsGroup>

      <DualSliderInput
        variant="label"
        name="price"
        min={min}
        max={max}
        step={STEP_NUM}
        startValue={startValue}
        endValue={endValue}
        onStartChange={handleStartChange}
        onEndChange={handleEndChange}
        format="currency"
      />
    </Group>
  )
}

export default FlightSingleFilterPrice
